(() => {
  if (customElements.get('ingredient-library')) {
    return;
  }

  const DESKTOP_MEDIA_QUERY = '(min-width: 1000px)';
  const SEARCH_DEBOUNCE_MS = 200;

  class IngredientLibrary extends HTMLElement {
    constructor() {
      super();

      this.activeCategoryId = null;
      this.searchQuery = '';
      this.currentPage = 1;
      this.searchTimeout = null;
      this.visibleCards = [];

      this.handleFilterClick = this.onFilterClick.bind(this);
      this.handleSearchInput = this.onSearchInput.bind(this);
      this.handlePaginationClick = this.onPaginationClick.bind(this);
      this.handleBreakpointChange = this.onBreakpointChange.bind(this);
      this.handleResultsToggle = this.onResultsToggle.bind(this);
    }

    connectedCallback() {
      this.setProperties();

      if (this.cards.length === 0) {
        this.renderEmptyState();
        return;
      }

      this.setEventListeners();
      this.render();
    }

    disconnectedCallback() {
      this.removeEventListeners();
      clearTimeout(this.searchTimeout);
    }

    setProperties() {
      this.itemsPerPage = Number.parseInt(this.dataset.itemsPerPage, 10) || 10;
      this.desktopQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
      this.filtersEl = this.querySelector('[data-filters]');
      this.searchInput = this.querySelector('[data-search-input]');
      this.resultsEl = this.querySelector('[data-results]');
      this.paginationEl = this.querySelector('[data-pagination]');
      this.paginationCountEl = this.querySelector('[data-pagination-count]');
      this.paginationPreviousButton = this.querySelector('[data-pagination-action="prev"]');
      this.paginationNextButton = this.querySelector('[data-pagination-action="next"]');
      this.pageItems = Array.from(this.querySelectorAll('[data-page-item]'));
      this.pageButtons = Array.from(this.querySelectorAll('[data-page-button]'));
      this.emptyEl = this.querySelector('[data-empty]');
      this.cards = Array.from(this.querySelectorAll('[data-ingredient-card]'));
    }

    setEventListeners() {
      this.filtersEl?.addEventListener('click', this.handleFilterClick);
      this.searchInput?.addEventListener('input', this.handleSearchInput);
      this.paginationEl?.addEventListener('click', this.handlePaginationClick);
      this.resultsEl?.addEventListener('toggle', this.handleResultsToggle, true);
      this.desktopQuery?.addEventListener('change', this.handleBreakpointChange);
    }

    removeEventListeners() {
      this.filtersEl?.removeEventListener('click', this.handleFilterClick);
      this.searchInput?.removeEventListener('input', this.handleSearchInput);
      this.paginationEl?.removeEventListener('click', this.handlePaginationClick);
      this.resultsEl?.removeEventListener('toggle', this.handleResultsToggle, true);
      this.desktopQuery?.removeEventListener('change', this.handleBreakpointChange);
    }

    onFilterClick(event) {
      const button = event.target.closest('[data-filter]');

      if (!button || !this.filtersEl?.contains(button)) {
        return;
      }

      const filterValue = button.dataset.filter;
      this.activeCategoryId = filterValue === 'all' ? null : filterValue;
      this.currentPage = 1;
      this.updateFilterButtons(button);
      this.render();
    }

    onSearchInput(event) {
      clearTimeout(this.searchTimeout);

      this.searchTimeout = setTimeout(() => {
        this.searchQuery = event.target.value.trim().toLowerCase();
        this.currentPage = 1;
        this.render();
      }, SEARCH_DEBOUNCE_MS);
    }

    onPaginationClick(event) {
      const control = event.target.closest('[data-page], [data-pagination-action]');

      if (!control || !this.paginationEl?.contains(control)) {
        return;
      }

      event.preventDefault();

      const totalPages = this.getTotalPages();

      if (control.dataset.paginationAction === 'prev') {
        this.currentPage = Math.max(1, this.currentPage - 1);
      } else if (control.dataset.paginationAction === 'next') {
        this.currentPage = Math.min(totalPages, this.currentPage + 1);
      } else if (control.dataset.page) {
        this.currentPage = Number.parseInt(control.dataset.page, 10) || 1;
      }

      this.render({ scrollToTop: true });
    }

    onBreakpointChange() {
      this.render();
    }

    onResultsToggle(event) {
      const card = event.target.closest('[data-ingredient-card]');

      if (!card || !card.open) {
        return;
      }

      this.visibleCards.forEach((rowCard) => {
        if (rowCard !== card) {
          rowCard.open = false;
        }
      });
    }

    updateFilterButtons(activeButton) {
      this.filtersEl?.querySelectorAll('[data-filter]').forEach((button) => {
        const isActive = button === activeButton;
        button.classList.toggle('ingredient-library__filter-btn--active', isActive);
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    render({ scrollToTop = false } = {}) {
      this.visibleCards = this.getVisibleCards();
      const totalPages = this.getTotalPages();

      if (this.currentPage > totalPages) {
        this.currentPage = totalPages;
      }

      if (this.visibleCards.length === 0) {
        this.renderEmptyState();
        return;
      }

      this.hideEmptyState();
      this.sortCardsInDom();

      if (this.isDesktop()) {
        this.renderDesktop();
      } else {
        this.renderMobile(totalPages);
      }

      if (scrollToTop) {
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    getVisibleCards() {
      const cards = this.cards.filter((card) => {
        const matchesCategory = !this.activeCategoryId || card.dataset.categoryId === this.activeCategoryId;
        const matchesSearch = !this.searchQuery || card.dataset.ingredientName?.includes(this.searchQuery);

        return matchesCategory && matchesSearch;
      });

      return this.sortCards(this.activeCategoryId ? cards : this.deduplicateCards(cards));
    }

    deduplicateCards(cards) {
      const seen = new Set();
      const uniqueCards = [];

      cards.forEach((card) => {
        const id = card.dataset.ingredientId;

        if (seen.has(id)) {
          return;
        }

        seen.add(id);
        uniqueCards.push(card);
      });

      return uniqueCards;
    }

    sortCards(cards) {
      return [...cards].sort((left, right) => {
        return (left.dataset.ingredientName || '').localeCompare(right.dataset.ingredientName || '', undefined, { sensitivity: 'base' });
      });
    }

    sortCardsInDom() {
      this.visibleCards.forEach((card) => {
        this.resultsEl?.append(card);
      });
    }

    getTotalPages() {
      return Math.max(1, Math.ceil(this.visibleCards.length / this.itemsPerPage));
    }

    isDesktop() {
      return this.desktopQuery?.matches ?? window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
    }

    renderEmptyState() {
      this.cards.forEach((card) => {
        card.hidden = true;
        card.open = false;
      });

      this.resultsEl?.setAttribute('hidden', '');
      this.emptyEl?.removeAttribute('hidden');
      this.hidePagination();
    }

    hideEmptyState() {
      this.resultsEl?.removeAttribute('hidden');
      this.emptyEl?.setAttribute('hidden', '');
    }

    renderDesktop() {
      this.cards.forEach((card) => {
        card.hidden = !this.visibleCards.includes(card);
        card.open = false;
      });

      this.visibleCards.forEach((card, index) => {
        card.open = index === 0;
      });

      this.hidePagination();
    }

    renderMobile(totalPages) {
      const startIndex = (this.currentPage - 1) * this.itemsPerPage;
      const endIndex = startIndex + this.itemsPerPage;

      this.cards.forEach((card) => {
        card.hidden = true;
        card.open = false;
      });

      this.visibleCards.forEach((card, index) => {
        const isVisibleOnPage = index >= startIndex && index < endIndex;

        card.hidden = !isVisibleOnPage;
        card.open = index === startIndex;
      });

      if (totalPages > 1) {
        this.renderPagination(totalPages, startIndex);
      } else {
        this.hidePagination();
      }
    }

    renderPagination(totalPages, startIndex) {
      if (!this.paginationEl) {
        return;
      }

      const viewedCount = Math.min(startIndex + this.itemsPerPage, this.visibleCards.length);
      const labelTemplate = this.paginationCountEl?.dataset.labelTemplate || '';
      const { start: visibleStart, end: visibleEnd } = this.getVisiblePageRange(totalPages);

      if (this.paginationCountEl) {
        this.paginationCountEl.textContent = labelTemplate
          .replace('@@VIEWED@@', String(viewedCount))
          .replace('@@TOTAL@@', String(this.visibleCards.length));
      }

      this.pageItems.forEach((item) => {
        const page = Number.parseInt(item.dataset.pageIndex, 10) || 0;
        item.hidden = page < visibleStart || page > visibleEnd || page > totalPages;
      });

      this.pageButtons.forEach((button) => {
        const page = Number.parseInt(button.dataset.page, 10) || 0;
        const isCurrent = page === this.currentPage;

        if (isCurrent) {
          button.setAttribute('aria-current', 'page');
        } else {
          button.removeAttribute('aria-current');
        }

        button.disabled = isCurrent;
      });

      this.updatePaginationArrow(this.paginationPreviousButton, this.currentPage <= 1);
      this.updatePaginationArrow(this.paginationNextButton, this.currentPage >= totalPages);
      this.paginationEl.removeAttribute('hidden');
    }

    getVisiblePageRange(totalPages, maxVisible = 4) {
      if (totalPages <= maxVisible) {
        return { start: 1, end: totalPages };
      }

      let start = Math.max(1, this.currentPage - 1);
      start = Math.min(start, totalPages - maxVisible + 1);

      return {
        start,
        end: start + maxVisible - 1
      };
    }

    updatePaginationArrow(button, isDisabled) {
      if (!button) {
        return;
      }

      button.disabled = isDisabled;
      button.classList.toggle('pagination__arrow--disabled', isDisabled);
    }

    hidePagination() {
      this.paginationEl?.setAttribute('hidden', '');
    }
  }

  customElements.define('ingredient-library', IngredientLibrary);
})();
